import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Axios interceptor: convert 429 (rate limit) into a user-friendly error message
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 429) {
      const message = error.response.data?.error || 'Too many requests. Please slow down and try again.';
      error.message = message;
    }
    return Promise.reject(error);
  }
);

const api = {
  // Create new session
 createSession: async () => {
    const response = await axios.post(`${API_URL}/session/create`);
    return response.data;
  },

  // Get session details
  getSession: async (code) => {
    const response = await axios.get(`${API_URL}/session/${code}`);
    return response.data;
  },

  // Submit vote
  submitVote: async (code, name, vote) => {
    const response = await axios.post(`${API_URL}/session/${code}/vote`, {
      name,
      vote
    });
    return response.data;
  },

  // Get results
  getResults: async (code) => {
    const response = await axios.get(`${API_URL}/session/${code}/results`);
    return response.data;
  },

  // Delete vote
  deleteVote: async (code, name) => {
    const response = await axios.delete(`${API_URL}/session/${code}/vote/${encodeURIComponent(name)}`);
    return response.data;
  },

  // Update session settings
  updateSettings: async (code, settings) => {
    const response = await axios.patch(`${API_URL}/session/${code}/settings`, settings);
    return response.data;
  },

  // Close session
  closeSession: async (code) => {
    const response = await axios.post(`${API_URL}/session/${code}/close`);
    return response.data;
  },

  // Reopen session
  reopenSession: async (code) => {
    const response = await axios.post(`${API_URL}/session/${code}/reopen`);
    return response.data;
  },

  // Get current vote for a voter (for re-voting)
  getMyVote: async (code, name) => {
    const response = await axios.get(`${API_URL}/session/${code}/my-vote`, {
      params: { name }
    });
    return response.data;
  },

  // Export session results (returns download URL)
  exportSessionUrl: (code, format = 'csv') => {
    return `${API_URL}/session/${code}/export?format=${format}`;
  },

  // Get sessions by codes (batch lookup)
  getSessionsBatch: async (codes) => {
    const response = await axios.post(`${API_URL}/sessions/batch`, { codes });
    return response.data;
  },

  // --- Admin Auth ---

  // Login as app admin
  adminLogin: async (password) => {
    const response = await axios.post(`${API_URL}/admin/login`, { password });
    return response.data;
  },

  // Get auth header (used by admin-panel requests)
  getAuthHeader: () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  // --- Admin Panel ---

  // Get all sessions (admin)
  adminGetSessions: async () => {
    const response = await axios.get(`${API_URL}/admin/sessions`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Get server stats (admin)
  adminGetStats: async () => {
    const response = await axios.get(`${API_URL}/admin/stats`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Force-close a session (admin)
  adminCloseSession: async (code) => {
    const response = await axios.post(`${API_URL}/admin/session/${code}/close`, {}, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Reopen a closed session (admin)
  adminReopenSession: async (code) => {
    const response = await axios.post(`${API_URL}/admin/session/${code}/reopen`, {}, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Delete a session (admin)
  adminDeleteSession: async (code) => {
    const response = await axios.delete(`${API_URL}/admin/session/${code}`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Purge all closed sessions (admin)
  adminPurgeClosed: async () => {
    const response = await axios.post(`${API_URL}/admin/purge`, {}, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Export results (admin) — get short-lived download token, then build URL
  adminGetExportToken: async () => {
    const response = await axios.post(`${API_URL}/admin/export-token`, {}, {
      headers: api.getAuthHeader()
    });
    return response.data.token;
  },

  adminExportUrl: (format, downloadToken) => {
    return `${API_URL}/admin/export?format=${format}&token=${downloadToken}`;
  },

  // Look up geolocation for an IP (admin)
  adminIpLookup: async (ip) => {
    const response = await axios.get(`${API_URL}/admin/ip/${ip}`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // --- Ban System ---

  // Get all banned IPs (admin)
  adminGetBans: async () => {
    const response = await axios.get(`${API_URL}/admin/bans`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Ban an IP (admin)
  adminBanIp: async (ip, reason = '') => {
    const response = await axios.post(`${API_URL}/admin/ban/${ip}`, { reason }, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Unban an IP (admin)
  adminUnbanIp: async (ip) => {
    const response = await axios.delete(`${API_URL}/admin/ban/${ip}`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // --- IP Allowlist (rate-limit bypass) ---

  // Get all allowlisted IPs (admin)
  adminGetAllowlist: async () => {
    const response = await axios.get(`${API_URL}/admin/allowlist`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Add an IP to the allowlist (admin)
  adminAllowIp: async (ip, note = '') => {
    const response = await axios.post(`${API_URL}/admin/allowlist/${ip}`, { note }, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Remove an IP from the allowlist (admin)
  adminUnallowIp: async (ip) => {
    const response = await axios.delete(`${API_URL}/admin/allowlist/${ip}`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // The admin's current public IP as seen by the server (admin)
  adminWhoami: async () => {
    const response = await axios.get(`${API_URL}/admin/whoami`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Get runtime settings (admin)
  adminGetSettings: async () => {
    const response = await axios.get(`${API_URL}/admin/settings`, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Update runtime settings (admin)
  adminUpdateSettings: async (settings) => {
    const response = await axios.patch(`${API_URL}/admin/settings`, settings, {
      headers: api.getAuthHeader()
    });
    return response.data;
  },

  // Search sessions (admin)
  adminSearchSessions: async (query, from, to) => {
    const params = {};
    if (query) params.q = query;
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await axios.get(`${API_URL}/admin/sessions/search`, {
      headers: api.getAuthHeader(),
      params
    });
    return response.data;
  },

  // Get analytics (admin)
  adminGetAnalytics: async (days = 30) => {
    const response = await axios.get(`${API_URL}/admin/analytics`, {
      headers: api.getAuthHeader(),
      params: { days }
    });
    return response.data;
  },

  // Get audit logs (admin)
  adminGetLogs: async (limit = 50, offset = 0) => {
    const response = await axios.get(`${API_URL}/admin/logs`, {
      headers: api.getAuthHeader(),
      params: { limit, offset }
    });
    return response.data;
  }
};

export default api;

